/*
 * LICENSE PROTECTED SOFTWARE
 *
 * Bu yazilim lisans korumasi altindadir.
 * Gecerli lisans olmadan kullanilmasi yasaktir.
 *
 * Kaynak kodunun izinsiz erisimi, kopyalanmasi veya dagitimi
 * hukuki yaptirimslara tabidir.
 * (c) 2026  @cemdemirci0101
 *  bilgi@cemdemirci.com
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution
 * of this file, via any medium, is strictly prohibited.
 * (c) 2026 All Rights Reserved.
 */

import React, { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import EmailNotifyToggle from '../components/EmailNotifyToggle';
import { usePageTitle } from '../hooks/usePageTitle';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Plus, Trash2, GripVertical, Save, ArrowLeft, Eye,
  Type, AlignLeft, Star, Hash, Image, ThumbsUp, List, Loader2,
  ChevronUp, ChevronDown, Settings2, Upload, X, Building, Shield,
} from 'lucide-react';
import { TARGET_AUDIENCES, KENT_KONSEYI_ANA_KADEME } from '../lib/utils';
import api, { workingGroupsAPI } from '../lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ImageLightbox = ({ src, alt, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm" onClick={onClose} data-testid="image-lightbox">
    <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors">
      <X className="w-6 h-6" />
    </button>
    <img src={src} alt={alt || ''} className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg shadow-2xl" onClick={e => e.stopPropagation()} />
  </div>
);

const QUESTION_TYPES = [
  { value: 'radio', label: 'Tek Secim', icon: Hash },
  { value: 'checkbox', label: 'Coklu Secim', icon: List },
  { value: 'text', label: 'Kisa Metin', icon: Type },
  { value: 'textarea', label: 'Uzun Metin', icon: AlignLeft },
  { value: 'rating', label: 'Derecelendirme (1-5)', icon: Star },
  { value: 'emoji', label: 'Yildiz Puanlama', icon: Star },
  { value: 'image_choice', label: 'Gorselli Secim', icon: Image },
  { value: 'yes_no', label: 'Evet / Hayir', icon: ThumbsUp },
  { value: 'ranking', label: 'Siralama', icon: GripVertical },
];

const emptyQuestion = () => ({
  _key: Date.now() + Math.random(),
  question_text: '',
  question_type: 'radio',
  is_required: false,
  description: '',
  options: [{ text: '', icon: '' }, { text: '', icon: '' }],
  settings: {},
});

export default function SurveyBuilderPage() {
  const navigate = useNavigate();
  usePageTitle('Anket Olustur');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: '', description: '', status: 'taslak',
    start_date: '', end_date: '', target_audience: [],
    target_group_ids: [],
    allow_update: false, is_anonymous: false,
  });
  const [questions, setQuestions] = useState([emptyQuestion()]);
  const [sendEmail, setSendEmail] = useState(false);
  const [emailAudiences, setEmailAudiences] = useState([]);
  const [meclisler, setMeclisler] = useState([]);
  const [allGroups, setAllGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);

  const updateForm = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const toggleTarget = (val) => {
    setForm(prev => {
      const arr = prev.target_audience || [];
      return { ...prev, target_audience: arr.includes(val) ? arr.filter(v => v !== val) : [...arr, val] };
    });
  };

  const showGroupSelector = form.target_audience.includes('calisma_gruplari') || form.target_audience.includes('meclisler');
  const isAnaKademeSelected = KENT_KONSEYI_ANA_KADEME.every(v => form.target_audience.includes(v));

  const toggleAnaKademe = () => {
    if (isAnaKademeSelected) {
      setForm(prev => ({ ...prev, target_audience: prev.target_audience.filter(a => !KENT_KONSEYI_ANA_KADEME.includes(a)) }));
    } else {
      setForm(prev => ({ ...prev, target_audience: [...new Set([...prev.target_audience, ...KENT_KONSEYI_ANA_KADEME])] }));
    }
  };

  const toggleGroupId = (id) => {
    setForm(prev => {
      const ids = prev.target_group_ids || [];
      return { ...prev, target_group_ids: ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id] };
    });
  };

  const toggleMeclisWithGroups = (meclisId) => {
    const mGroups = allGroups.filter(g => g.meclis_id === meclisId);
    const mIds = [meclisId, ...mGroups.map(g => g.id)];
    setForm(prev => {
      const allChecked = mIds.every(id => (prev.target_group_ids || []).includes(id));
      if (allChecked) {
        return { ...prev, target_group_ids: (prev.target_group_ids || []).filter(id => !mIds.includes(id)) };
      }
      return { ...prev, target_group_ids: [...new Set([...(prev.target_group_ids || []), ...mIds])] };
    });
  };

  React.useEffect(() => {
    if (showGroupSelector && meclisler.length === 0) {
      setLoadingGroups(true);
      (async () => {
        try {
          const resp = await workingGroupsAPI.getMeclisler();
          setMeclisler(resp.data);
          const groups = [];
          for (const m of resp.data) {
            const detail = await workingGroupsAPI.getMeclisDetail(m.id);
            for (const wg of (detail.data.working_groups || [])) {
              groups.push({ ...wg, meclis_name: m.name, meclis_id: m.id });
            }
          }
          setAllGroups(groups);
        } catch { /* ignore */ }
        finally { setLoadingGroups(false); }
      })();
    }
  }, [showGroupSelector]);

  // Question management
  const addQuestion = () => setQuestions(prev => [...prev, emptyQuestion()]);

  const removeQuestion = (idx) => setQuestions(prev => prev.filter((_, i) => i !== idx));

  const updateQuestion = (idx, key, value) => {
    setQuestions(prev => prev.map((q, i) => i === idx ? { ...q, [key]: value } : q));
  };

  const moveQuestion = (idx, dir) => {
    setQuestions(prev => {
      const arr = [...prev];
      const targetIdx = idx + dir;
      if (targetIdx < 0 || targetIdx >= arr.length) return arr;
      [arr[idx], arr[targetIdx]] = [arr[targetIdx], arr[idx]];
      return arr;
    });
  };

  // Option management
  const addOption = (qIdx) => {
    setQuestions(prev => prev.map((q, i) => i === qIdx ? { ...q, options: [...(q.options || []), { text: '', icon: '' }] } : q));
  };

  const removeOption = (qIdx, oIdx) => {
    setQuestions(prev => prev.map((q, i) => i === qIdx ? { ...q, options: (q.options || []).filter((_, j) => j !== oIdx) } : q));
  };

  const updateOption = (qIdx, oIdx, key, value) => {
    setQuestions(prev => prev.map((q, i) => {
      if (i !== qIdx) return q;
      const opts = [...(q.options || [])];
      opts[oIdx] = { ...opts[oIdx], [key]: value };
      return { ...q, options: opts };
    }));
  };

  // Conditional logic
  const updateConditional = (qIdx, condData) => {
    setQuestions(prev => prev.map((q, i) => {
      if (i !== qIdx) return q;
      return { ...q, settings: { ...(q.settings || {}), conditional_logic: condData || null } };
    }));
  };

  const hasOptions = (type) => ['radio', 'checkbox', 'image_choice', 'ranking'].includes(type);

  // Image upload for options
  const [uploading, setUploading] = useState({});
  const [lightboxImg, setLightboxImg] = useState(null);
  const fileInputRefs = useRef({});

  const handleOptionImageUpload = async (qIdx, oIdx, file) => {
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/gif'];
    if (!allowed.includes(file.type)) {
      toast.error('Sadece JPG, PNG ve GIF formatlari desteklenir');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Dosya boyutu maksimum 10MB olabilir');
      return;
    }
    const key = `${qIdx}-${oIdx}`;
    setUploading(prev => ({ ...prev, [key]: true }));
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/surveys/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      updateOption(qIdx, oIdx, 'image', res.data.url);
      toast.success('Gorsel yuklendi');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gorsel yuklenemedi');
    } finally {
      setUploading(prev => ({ ...prev, [key]: false }));
    }
  };

  const removeOptionImage = (qIdx, oIdx) => {
    updateOption(qIdx, oIdx, 'image', null);
  };

  const handleSave = async (publishStatus) => {
    if (!form.title.trim()) { toast.error('Anket basligi zorunludur'); return; }
    if (questions.length === 0) { toast.error('En az 1 soru ekleyin'); return; }
    for (const q of questions) {
      if (!q.question_text.trim()) { toast.error('Tum soru metinleri doldurulmalidir'); return; }
      if (hasOptions(q.question_type) && (!q.options || q.options.length < 2 || q.options.some(o => !o.text.trim()))) {
        toast.error(`"${q.question_text}" sorusu icin en az 2 secenek ve tum secenek metinleri gerekli`); return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        status: publishStatus || form.status,
        send_notification_email: publishStatus === 'aktif' ? sendEmail : false,
        email_target_audiences: sendEmail ? (emailAudiences.length > 0 ? emailAudiences : form.target_audience) : null,
        questions: questions.map((q, i) => ({
          question_text: q.question_text,
          question_type: q.question_type,
          is_required: q.is_required,
          description: q.description || null,
          image: q.image || null,
          order_index: i,
          settings: q.settings || null,
          options: hasOptions(q.question_type) ? q.options : null,
        })),
      };
      await api.post('/surveys', payload);
      toast.success(publishStatus === 'aktif' ? 'Anket yayinlandi!' : 'Anket taslak olarak kaydedildi');
      navigate('/panel/anketler');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Anket kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-5xl mx-auto" data-testid="survey-builder">
      {/* Back */}
      <div className="flex items-center">
        <Button variant="ghost" size="sm" onClick={() => navigate('/panel/anketler')} data-testid="back-btn" className="text-slate-600 hover:text-slate-900">
          <ArrowLeft className="w-4 h-4 mr-1" /> Anketler
        </Button>
      </div>

      {/* Page Header */}
      <div className="overflow-hidden rounded-xl border-0 shadow-lg">
        <div className="bg-gradient-to-r from-violet-800 via-purple-900 to-violet-800 px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center">
                <List className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">Yeni Anket Olustur</h1>
                <p className="text-sm text-white/50">Sorularinizi ekleyin ve hedef kitlenizi secin</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => handleSave('taslak')} disabled={saving} data-testid="save-draft-btn" className="bg-white/10 hover:bg-white/20 text-white border-white/20">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Taslak Kaydet
              </Button>
              <Button onClick={() => handleSave('aktif')} disabled={saving} data-testid="publish-btn" className="bg-white text-violet-900 hover:bg-white/90">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />} Yayinla
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sol - Ana Form */}
        <div className="lg:col-span-2 space-y-5">
          {/* Survey Info */}
          <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4 shadow-sm">
            <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-violet-600 text-white flex items-center justify-center text-xs font-bold">1</span>
              Anket Bilgileri
            </h2>
            <div>
              <label className="text-sm font-medium text-slate-600">Baslik *</label>
              <Input value={form.title} onChange={e => updateForm('title', e.target.value)} placeholder="Anket basligi" data-testid="survey-title-input" className="mt-1.5" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-600">Aciklama</label>
              <textarea
                className="w-full mt-1.5 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-500 outline-none resize-none"
                rows={3} value={form.description} onChange={e => updateForm('description', e.target.value)}
                placeholder="Anket hakkinda kisa aciklama" data-testid="survey-desc-input"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-600">Baslangic Tarihi</label>
                <Input type="date" value={form.start_date} onChange={e => updateForm('start_date', e.target.value)} data-testid="survey-start-date" className="mt-1.5" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-600">Bitis Tarihi</label>
                <Input type="date" value={form.end_date} onChange={e => updateForm('end_date', e.target.value)} data-testid="survey-end-date" className="mt-1.5" />
              </div>
            </div>
          </div>

          {/* Target Audience */}
          <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-3 shadow-sm">
            <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-violet-600 text-white flex items-center justify-center text-xs font-bold">2</span>
              Hedef Kitle
            </h2>
            <p className="text-xs text-slate-500">Ankete katilacak gruplari secin</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {TARGET_AUDIENCES.map(t => {
                const checked = (form.target_audience || []).includes(t.value);
                return (
                  <button key={t.value} type="button" onClick={() => toggleTarget(t.value)}
                    data-testid={`target-${t.value}`}
                    className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg border text-sm text-left transition-all ${checked ? 'border-violet-500 bg-violet-50 text-violet-800 font-medium' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'}`}>
                    <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${checked ? 'border-violet-600 bg-violet-600' : 'border-slate-300'}`}>
                      {checked && <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                    </div>
                    {t.label}
                  </button>
                );
              })}
            </div>
            <button type="button" onClick={toggleAnaKademe}
              className={`w-full flex items-center justify-center gap-2.5 px-4 py-3 rounded-lg border-2 border-dashed text-sm font-semibold transition-all ${isAnaKademeSelected ? 'border-indigo-500 bg-indigo-50 text-indigo-800' : 'border-slate-300 bg-slate-50 text-slate-600 hover:border-indigo-400'}`}>
              <Building className={`w-4 h-4 ${isAnaKademeSelected ? 'text-indigo-600' : 'text-slate-400'}`} />
              Kent Konseyi Ana Kademe
              <span className={`text-xs px-2 py-0.5 rounded-full ${isAnaKademeSelected ? 'bg-indigo-200 text-indigo-800' : 'bg-slate-200 text-slate-500'}`}>
                {isAnaKademeSelected ? 'Secildi' : 'Toplu Sec'}
              </span>
            </button>
            {isAnaKademeSelected && <p className="text-xs text-indigo-600 text-center">Yurutme Kurulu + Genel Sekreter + Kent Konseyi Baskani secildi</p>}
          </div>

          {/* Grup Hedefleme */}
          {showGroupSelector && (
            <div className="bg-white rounded-xl border border-amber-200 p-6 space-y-3 shadow-sm">
              <h2 className="text-base font-semibold text-amber-800 flex items-center gap-2">
                <Shield className="w-5 h-5 text-amber-600" /> Meclis / Calisma Grubu Hedefleme
              </h2>
              <p className="text-xs text-amber-700">Sectiginiz gruplara yalnizca onayli uyeler erisebilir.</p>
              {loadingGroups ? (
                <div className="flex items-center gap-2 py-6 justify-center">
                  <Loader2 className="w-4 h-4 animate-spin text-amber-600" />
                  <span className="text-sm text-amber-700">Gruplar yukleniyor...</span>
                </div>
              ) : (
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {meclisler.map(meclis => {
                    const mGroups = allGroups.filter(g => g.meclis_id === meclis.id);
                    const mIds = [meclis.id, ...mGroups.map(g => g.id)];
                    const allChecked = mIds.every(id => (form.target_group_ids || []).includes(id));
                    return (
                      <div key={meclis.id} className="border border-amber-100 rounded-lg bg-white p-3">
                        <div className="flex items-center space-x-2 mb-2">
                          <Checkbox checked={allChecked} onCheckedChange={() => toggleMeclisWithGroups(meclis.id)} />
                          <span className="text-sm font-bold text-amber-900">{meclis.name}</span>
                        </div>
                        {mGroups.length > 0 && (
                          <div className="ml-6 space-y-1.5">
                            {mGroups.map(g => (
                              <div key={g.id} className="flex items-center space-x-2">
                                <Checkbox checked={(form.target_group_ids || []).includes(g.id)} onCheckedChange={() => toggleGroupId(g.id)} />
                                <span className="text-xs text-slate-700">{g.name}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Questions */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-violet-600 text-white flex items-center justify-center text-xs font-bold">3</span>
                Sorular ({questions.length})
              </h2>
              <Button variant="outline" size="sm" onClick={addQuestion} data-testid="add-question-btn" className="border-violet-200 text-violet-600 hover:bg-violet-50">
                <Plus className="w-4 h-4 mr-1" /> Soru Ekle
              </Button>
            </div>

            {questions.map((q, qIdx) => {
              const typeInfo = QUESTION_TYPES.find(t => t.value === q.question_type);
              const TypeIcon = typeInfo?.icon || Hash;
              return (
                <div key={q._key} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid={`question-block-${qIdx}`}>
                  {/* Question number bar */}
                  <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-50 border-b">
                    <div className="flex flex-col gap-0.5">
                      <button onClick={() => moveQuestion(qIdx, -1)} disabled={qIdx === 0} className="p-0.5 text-slate-400 hover:text-slate-600 disabled:opacity-30"><ChevronUp className="w-3.5 h-3.5" /></button>
                      <button onClick={() => moveQuestion(qIdx, 1)} disabled={qIdx === questions.length - 1} className="p-0.5 text-slate-400 hover:text-slate-600 disabled:opacity-30"><ChevronDown className="w-3.5 h-3.5" /></button>
                    </div>
                    <Badge className="bg-violet-100 text-violet-700 border-violet-200 text-xs">Soru {qIdx + 1}</Badge>
                    <div className="flex items-center gap-1.5 text-xs text-slate-500 ml-1">
                      <TypeIcon className="w-3.5 h-3.5" />
                      {typeInfo?.label}
                    </div>
                    <div className="ml-auto flex items-center gap-2">
                      <label className="flex items-center gap-1.5 text-xs text-slate-500 cursor-pointer">
                        <Checkbox checked={q.is_required} onCheckedChange={v => updateQuestion(qIdx, 'is_required', v)} data-testid={`q-required-${qIdx}`} />
                        Zorunlu
                      </label>
                      <button onClick={() => removeQuestion(qIdx)} className="p-1 text-slate-400 hover:text-red-500 transition-colors" data-testid={`q-delete-${qIdx}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <div className="p-5 space-y-4">
                    {/* Type & Text */}
                    <div className="flex items-center gap-3">
                      <Select value={q.question_type} onValueChange={v => updateQuestion(qIdx, 'question_type', v)}>
                        <SelectTrigger className="w-48" data-testid={`q-type-${qIdx}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {QUESTION_TYPES.map(t => (
                            <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Input
                      value={q.question_text} onChange={e => updateQuestion(qIdx, 'question_text', e.target.value)}
                      placeholder="Soru metni *" className="font-medium" data-testid={`q-text-${qIdx}`}
                    />
                    <Input
                      value={q.description || ''} onChange={e => updateQuestion(qIdx, 'description', e.target.value)}
                      placeholder="Aciklama (opsiyonel)" className="text-sm" data-testid={`q-desc-${qIdx}`}
                    />

                    {/* Conditional Logic */}
                    {qIdx > 0 && (
                      <div className="flex items-center gap-2 text-xs p-2.5 bg-slate-50 rounded-lg">
                        <Settings2 className="w-3.5 h-3.5 text-slate-400" />
                        <span className="text-slate-400">Sartli gosterim:</span>
                        <Select
                          value={q.settings?.conditional_logic?.question_index?.toString() || 'none'}
                          onValueChange={v => {
                            if (v === 'none') { updateConditional(qIdx, null); return; }
                            updateConditional(qIdx, { question_index: parseInt(v), operator: 'equals', value: '' });
                          }}
                        >
                          <SelectTrigger className="w-40 h-7 text-xs"><SelectValue placeholder="Her zaman goster" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Her zaman goster</SelectItem>
                            {questions.slice(0, qIdx).map((pq, pi) => (
                              <SelectItem key={pi} value={pi.toString()}>Soru {pi + 1}: {pq.question_text?.slice(0, 20) || '...'}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {q.settings?.conditional_logic && (
                          <>
                            <span className="text-slate-400">=</span>
                            <Input
                              className="w-32 h-7 text-xs"
                              placeholder="Deger"
                              value={q.settings?.conditional_logic?.value || ''}
                              onChange={e => updateConditional(qIdx, { ...q.settings.conditional_logic, value: e.target.value })}
                            />
                          </>
                        )}
                      </div>
                    )}

                    {/* Options */}
                    {hasOptions(q.question_type) && (
                      <div className="space-y-3 pl-4 border-l-2 border-violet-100">
                        {(q.options || []).map((opt, oIdx) => {
                          const uploadKey = `${qIdx}-${oIdx}`;
                          const isUploading = uploading[uploadKey];
                          return (
                            <div key={oIdx} className="space-y-1.5">
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-slate-400 w-5 font-mono">{oIdx + 1}.</span>
                                <Input
                                  value={opt.text} onChange={e => updateOption(qIdx, oIdx, 'text', e.target.value)}
                                  placeholder={`Secenek ${oIdx + 1}`} className="flex-1 h-8 text-sm" data-testid={`q-${qIdx}-opt-${oIdx}`}
                                />
                                <input
                                  type="file" accept=".jpg,.jpeg,.png,.gif" className="hidden"
                                  ref={el => { fileInputRefs.current[uploadKey] = el; }}
                                  onChange={e => { handleOptionImageUpload(qIdx, oIdx, e.target.files?.[0]); e.target.value = ''; }}
                                  data-testid={`q-${qIdx}-opt-${oIdx}-file`}
                                />
                                <button type="button" onClick={() => fileInputRefs.current[uploadKey]?.click()} disabled={isUploading}
                                  className={`p-1.5 rounded-md border transition-colors ${opt.image ? 'border-violet-300 bg-violet-50 text-violet-600' : 'border-slate-200 text-slate-400 hover:text-violet-500 hover:border-violet-200'}`}
                                  title="Gorsel ekle" data-testid={`q-${qIdx}-opt-${oIdx}-upload`}>
                                  {isUploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Image className="w-3.5 h-3.5" />}
                                </button>
                                {(q.options || []).length > 2 && (
                                  <button onClick={() => removeOption(qIdx, oIdx)} className="p-1 text-slate-400 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                                )}
                              </div>
                              {opt.image && (
                                <div className="relative ml-7 inline-block">
                                  <img src={opt.image.startsWith('/') ? `${API_URL}${opt.image}` : opt.image} alt={opt.text || 'Secenek gorseli'}
                                    className="h-16 w-auto rounded-lg border border-slate-200 object-cover cursor-zoom-in"
                                    onClick={() => setLightboxImg(opt.image.startsWith('/') ? `${API_URL}${opt.image}` : opt.image)}
                                    data-testid={`q-${qIdx}-opt-${oIdx}-preview`} />
                                  <button type="button" onClick={() => removeOptionImage(qIdx, oIdx)}
                                    className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center shadow-sm hover:bg-red-600"
                                    data-testid={`q-${qIdx}-opt-${oIdx}-remove-img`}><X className="w-3 h-3" /></button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                        <Button variant="ghost" size="sm" className="text-xs text-violet-600 hover:bg-violet-50" onClick={() => addOption(qIdx)} data-testid={`q-${qIdx}-add-opt`}>
                          <Plus className="w-3 h-3 mr-1" /> Secenek Ekle
                        </Button>
                      </div>
                    )}

                    {/* Rating preview */}
                    {(q.question_type === 'rating' || q.question_type === 'emoji') && (
                      <div className="flex items-center gap-1 pl-4">
                        {[1,2,3,4,5].map(n => (
                          <Star key={n} className={`w-6 h-6 ${n <= 3 ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} />
                        ))}
                        <span className="text-xs text-slate-400 ml-2">1-5 arasi puanlama</span>
                      </div>
                    )}

                    {/* Yes/No preview */}
                    {q.question_type === 'yes_no' && (
                      <div className="flex gap-3 pl-4">
                        <span className="px-4 py-1.5 rounded-lg border border-green-200 bg-green-50 text-sm text-green-700">Evet</span>
                        <span className="px-4 py-1.5 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700">Hayir</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            <Button variant="outline" className="w-full border-dashed border-violet-200 text-violet-600 hover:bg-violet-50" onClick={addQuestion} data-testid="add-question-bottom-btn">
              <Plus className="w-4 h-4 mr-2" /> Yeni Soru Ekle
            </Button>
          </div>
        </div>

        {/* Sag - Sidebar */}
        <div className="space-y-5">
          {/* Ayarlar */}
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm space-y-4">
            <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <Settings2 className="w-4 h-4 text-violet-500" /> Anket Ayarlari
            </h3>
            <label className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 cursor-pointer hover:border-slate-300 transition-all">
              <Checkbox checked={form.allow_update} onCheckedChange={v => updateForm('allow_update', v)} data-testid="allow-update-check" />
              <div>
                <span className="text-sm font-medium text-slate-700">Cevap Guncelleme</span>
                <p className="text-xs text-slate-400">Katilimcilar yanitlarini degistirebilir</p>
              </div>
            </label>
            <label className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 cursor-pointer hover:border-slate-300 transition-all">
              <Checkbox checked={form.is_anonymous} onCheckedChange={v => updateForm('is_anonymous', v)} data-testid="anonymous-check" />
              <div>
                <span className="text-sm font-medium text-slate-700">Anonim Anket</span>
                <p className="text-xs text-slate-400">Katilimci kimlikleri gizlenir</p>
              </div>
            </label>
          </div>

          {/* Ozet */}
          <div className="bg-white rounded-xl border border-dashed border-slate-300 p-5 shadow-sm space-y-3">
            <h3 className="text-sm font-semibold text-slate-500">Ozet</h3>
            <div className="space-y-2.5">
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-8 rounded-full bg-violet-500" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-900 truncate">{form.title || 'Anket basligi...'}</p>
                  <p className="text-xs text-slate-400 truncate">{form.description ? form.description.substring(0, 60) + '...' : 'Aciklama...'}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                <Badge variant="outline" className="text-xs">{questions.length} soru</Badge>
                {form.start_date && <Badge variant="outline" className="text-xs">Baslangic: {form.start_date}</Badge>}
                {form.end_date && <Badge variant="outline" className="text-xs">Bitis: {form.end_date}</Badge>}
              </div>
              {(form.target_audience || []).length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {form.target_audience.map(t => (
                    <span key={t} className="text-[11px] px-2 py-0.5 rounded-full bg-violet-50 text-violet-700">{TARGET_AUDIENCES.find(o => o.value === t)?.label || t}</span>
                  ))}
                </div>
              )}
              <div className="flex flex-wrap gap-2 text-xs text-slate-400">
                {form.allow_update && <span className="flex items-center gap-1"><Settings2 className="w-3 h-3" /> Guncellenebilir</span>}
                {form.is_anonymous && <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> Anonim</span>}
              </div>
            </div>
          </div>

          {/* Soru Tipleri Rehberi */}
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Soru Tipleri</h3>
            <div className="space-y-2">
              {QUESTION_TYPES.map(t => {
                const TIcon = t.icon;
                return (
                  <div key={t.value} className="flex items-center gap-2.5 text-xs text-slate-500">
                    <TIcon className="w-3.5 h-3.5 text-violet-400 flex-shrink-0" />
                    <span>{t.label}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* E-posta Bildirim */}
          <EmailNotifyToggle
            sendEmail={sendEmail} setSendEmail={setSendEmail}
            emailAudiences={emailAudiences} setEmailAudiences={setEmailAudiences}
            defaultAudiences={form.target_audience}
          />

          {/* Actions */}
          <div className="space-y-2">
            <Button onClick={() => handleSave('aktif')} disabled={saving} className="w-full bg-violet-600 hover:bg-violet-700 shadow-sm" data-testid="publish-bottom-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
              Yayinla
            </Button>
            <Button variant="outline" onClick={() => handleSave('taslak')} disabled={saving} className="w-full" data-testid="save-draft-bottom-btn">
              <Save className="w-4 h-4 mr-2" /> Taslak Kaydet
            </Button>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxImg && <ImageLightbox src={lightboxImg} alt="Secenek gorseli" onClose={() => setLightboxImg(null)} />}
    </div>
  );
}
